import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps } from "@store/Components/Common/Block";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { actionLinkStyles, lineEllipsisStyles, linkStyles, printButton } from "@store/theme/mixins";
import { addToListLinkProps } from "@store/theme/props/addToListLink";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { css } from "styled-components";

interface DetailContentProps {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    verticalWrapperGridItem?: GridItemProps;
    mobileVerticalContainer?: GridContainerProps;
    mobileVerticalCarouselGridItem?: GridItemProps;
    mobileVerticalCarouselImageGridItem?: GridItemProps;
    mobileCarouselGridItem?: GridItemProps;
    productShortDescriptionWrapper?: InjectableCss;
    productShortDescription?: TypographyPresentationProps;
    productReadMoreLink?: LinkPresentationProps;
    productSpecificationsgridItem?: GridItemProps;
    productShortSpecificationsText?: TypographyPresentationProps;
    productShortSpecificationsLabel?: InjectableCss;
    productSeeAllSpecificationsLink?: LinkPresentationProps;
    productVariantGridItem?: GridItemProps;
    priceGridItem?: GridItemProps;
    productStartingAtText?: TypographyPresentationProps;
    availabilityGridItem?: GridItemProps;
    qtyOnHandText?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    actionsInnerWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
}
interface ProductNavigation {
    link?: LinkPresentationProps;
    nav?: InjectableCss;
}
interface DetailProps {
    block?: BlockProps;
    printLinkWrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    container?: GridContainerProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalCarouselImageGridItem?: GridItemProps;
    carouselGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    content?: DetailContentProps;
    productNavigation?: ProductNavigation;
}
export interface StoreProductDetailsViewStyles {
    detail?: DetailProps;
    accordionBlock?: BlockProps;
}

const printLink = printButton();

const printLinkStyle: LinkPresentationProps = {
    icon: printLink.icon,
    css: css`
        ${printLink.css}
        position: absolute;
        right: 0;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        bottom: 0;
                    `,
                    null,
                    css`
                        bottom: 10px;
                    `,
                    css`
                        bottom: ${COMMON.spacing.content[3]};
                    `,
                    css`
                        bottom: ${COMMON.spacing.content[4]};
                    `,
                ],
                "min",
            )}
    `,
};

const anchorLinkStyles = () => {
    return css`
        ${linkStyles("primary", "primary.dark", true)}
        font-weight: ${TYPOGRAPHY.weight.bold};
        margin-top: 0.2em;
    `;
};

const descriptionMaxHeight = 72;
export const descriptionWrapperMaxHeight = descriptionMaxHeight - 5;

export const storeProductDetailsViewStyles: StoreProductDetailsViewStyles = {
    detail: {
        printLinkWrapper: {
            css: css`
                z-index: 2;
                position: relative;
                width: 100%;
            `,
        },
        printLink: printLinkStyle,
        container: {
            gap: 30,
        },
        verticalCarouselGridItem: {
            width: [0, 0, 0, 0, 1],
            css: css`
                flex-direction: column;
            `,
        },
        verticalCarouselImageGridItem: {
            width: [0, 0, 0, 0, 6],
            css: css`
                justify-content: center;
            `,
        },
        carouselGridItem: {
            width: [0, 0, 6, 6, 0],
            css: css`
                flex-direction: column;
            `,
        },
        contentGridItem: { width: [12, 12, 6, 6, 5] },
        content: {
            container: { gap: 12 },
            gridItem: { width: 12 },
            priceGridItem: {
                width: 12,
                css: css`
                    flex-direction: column;
                `,
            },
            productStartingAtText: {
                size: TYPOGRAPHY.size.smaller,
            },
            availabilityGridItem: {
                width: 12,
                css: css`
                    flex-direction: column;
                `,
            },
            qtyOnHandText: {
                forwardAs: "p",
                size: TYPOGRAPHY.size.smaller,
                weight: TYPOGRAPHY.weight.bold,
            },
            verticalWrapperGridItem: {
                width: [0, 12, 0, 0, 0],
            },
            mobileVerticalContainer: {
                css: css`
                    padding: 30px 0;
                `,
            },
            mobileVerticalCarouselGridItem: {
                width: [0, 2, 0, 0, 0],
            },
            mobileVerticalCarouselImageGridItem: {
                width: [0, 10, 0, 0, 0],
                css: css`
                    justify-content: center;
                `,
            },
            mobileCarouselGridItem: {
                width: [12, 0, 0, 0, 0],
                css: css`
                    flex-direction: column;
                    padding: 24px 6px;
                `,
            },
            productShortDescriptionWrapper: {
                css: css`
                    display: block;
                    overflow: hidden;
                    max-height: ${descriptionMaxHeight}px;
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                `,
            },
            productShortDescription: {
                css: css`
                    ${lineEllipsisStyles(3)}
                `,
            },
            productReadMoreLink: {
                css: css`
                    ${anchorLinkStyles}
                `,
            },
            productSpecificationsgridItem: {
                width: 12,
                css: css`
                    flex-direction: column;
                    padding-top: 18px;
                `,
            },
            productShortSpecificationsText: {
                forwardAs: "p",
            },
            productShortSpecificationsLabel: {
                css: css`
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    margin-right: ${TYPOGRAPHY.spacing.larger};

                    ${props =>
                        breakpointValuesMediaQueries(
                            props.theme,
                            [{ values: ["block", "inline-flex"], properties: ["display"] }],
                            mediaOptions.min,
                        )}
                `,
            },
            productSeeAllSpecificationsLink: {
                css: css`
                    ${anchorLinkStyles}
                `,
            },
            productVariantGridItem: {
                width: 12,
                css: css`
                    padding-top: 18px;
                `,
            },
            actionsWrapper: {
                css: css`
                    display: flex;
                    flex-flow: wrap;
                    align-items: flex-end;
                    width: 100%;
                    padding-top: 0.75em;
                    margin-top: 0.75em;
                    border-top: 1px solid ${getColor("common.border")};
                `,
            },
            actionsInnerWrapper: {
                css: css`
                    display: flex;
                    padding: 0.5em 0.5em 0.5em 0;
                `,
            },
            addToListLink: addToListLinkProps,
        },
        productNavigation: {
            nav: {
                css: css`
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-wrap: wrap;
                    min-height: 45px;
                    background-color: ${getColor("common.accent")};
                    ${props =>
                        breakpointValuesMediaQueries(
                            props.theme,
                            [
                                { values: ["7px 13px", "7px 11px", "7px 1px", null], properties: ["padding"] },
                                { values: ["flex", "flex", null], properties: ["display"] },
                            ],
                            mediaOptions.min,
                        )}

                    button {
                        ${props =>
                            breakpointValuesMediaQueries(
                                props.theme,
                                [{ values: ["0 8px", "0 10px", "0 20px", "0 28px", "0 36px"], properties: ["margin"] }],
                                mediaOptions.min,
                            )}
                    }
                `,
            },
            link: {
                css: css`
                    ${linkStyles("text", "primary", true)}
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    margin-top: 0.2em;
                    text-transform: uppercase;
                    font-size: ${TYPOGRAPHY.size.smallest};
                    letter-spacing: ${TYPOGRAPHY.letterSpacing.larger};
                `,
            },
        },
    },
    accordionBlock: {},
};
